import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import InfoComponent from '../MapInfoWindow/';
import { markerProps } from '../MapMarker/propTypes';
import {
    Marker,
    MarkerClusterer,
    HeatmapLayer,
    InfoWindow,
    GoogleMap,
    Polyline,
} from '@react-google-maps/api';
import './styles.scss';
import { THEME } from '../../common/constants';
import { MAP_STYLE_LIGHT } from './constants';

const options = {
    imagePath:
        'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
};

/**
 * Google Map Integration
 * @see https://react-google-maps-api-docs.netlify.com/#infobox
 */
function Map({
    id,
    zoom,
    center,
    dataPoints,
    isLoading,
    showHeatMap,
    drawPolyline,
    // showError,
    showMarkerWithPolyline,
    markerPoints,
    markerClickable,
    ...restProps
}) {
    const [showWindow, setShowWindow] = useState(false);
    const [windowContent, setWindowContent] = useState(
        <h1>Info Window Content</h1>,
    );
    const [windowPosition, setWindowPosition] = useState(center);

    useEffect(() => {
        setShowWindow(false);
    }, [dataPoints]);

    const renderMapContent = () => {
        return drawPolyline ? renderPolyline() : renderMarkers();
    };

    const renderPolyline = () => {
        return (
            <>
                <Polyline
                    path={dataPoints}
                    options={{
                        strokeColor: THEME.PRIMARY_BLUE,
                    }}
                />
                {showMarkerWithPolyline &&
                    markerPoints
                        .filter((s1) => s1.marker)
                        .map((markerPoint, index) => (
                            <Marker
                                key={`k_${markerPoint.lat}_${index}`}
                                position={{
                                    lat: markerPoint.lat,
                                    lng: markerPoint.lng,
                                }}
                                className="mapMarker"
                                icon={markerPoint.marker}
                                onClick={() => {
                                    if (markerClickable) {
                                        setWindowPosition({
                                            lat: markerPoint.lat,
                                            lng: markerPoint.lng,
                                        });
                                        setWindowContent(
                                            <InfoComponent
                                                thumbnail={
                                                    markerPoint.thumbnail
                                                }
                                            />,
                                        );
                                        setShowWindow(true);
                                    }
                                }}
                            />
                        ))}
            </>
        );
    };

    const renderMarkers = () => {
        return (
            <MarkerClusterer options={options}>
                {(clusterer) =>
                    dataPoints.map(
                        ({ id, icon, tags, thumbnail, position }, index) => (
                            <Marker
                                key={`k_${id}_${index}`}
                                position={position}
                                className="mapMarker"
                                icon={icon}
                                clusterer={clusterer}
                                onClick={() => {
                                    if (markerClickable) {
                                        setWindowPosition(position);
                                        setWindowContent(
                                            <InfoComponent
                                                thumbnail={thumbnail}
                                                tags={tags}
                                            />,
                                        );
                                        setShowWindow(true);
                                    }
                                }}
                            />
                        ),
                    )
                }
            </MarkerClusterer>
        );
    };

    const renderInfoWindow = () => {
        return showWindow ? (
            <InfoWindow
                position={windowPosition}
                onCloseClick={() => setShowWindow(false)}
                className="info-window"
            >
                {windowContent}
            </InfoWindow>
        ) : null;
    };

    let renderContent = null;

    if (showHeatMap) {
        const google = window.google;
        const hmPoints = dataPoints.map(
            (pointer) =>
                new google.maps.LatLng(
                    pointer.position.lat,
                    pointer.position.lng,
                ),
        );

        renderContent = (
            <GoogleMap
                id={id}
                options={{
                    styles: MAP_STYLE_LIGHT,
                    streetViewControl: false,
                    mapTypeControl: false,
                }}
                zoom={zoom}
                center={center}
                {...restProps}
            >
                <HeatmapLayer
                    data={hmPoints}
                    options={{
                        radius: 2,
                        opacity: 1,
                        dissipating: false,
                        gradient: [
                            'rgba(2, 122, 255, 0)',
                            'rgba(2, 122, 255, 0.3)',
                            'rgba(63, 143, 194, 0.4)',
                            'rgba(131, 167, 125, 0.5)',
                            'rgba(193, 189, 63, 0.6)',
                            'rgba(240, 173, 14, 0.7)',
                            'rgba(227, 139, 26, 0.8)',
                            'rgba(219, 110, 34, 0.9)',
                            'rgba(210, 80, 42, 0.9)',
                            'rgba(201, 50, 50, 1)',
                        ],
                    }}
                />
            </GoogleMap>
        );
    } else {
        renderContent = (
            <GoogleMap
                id={id}
                zoom={zoom}
                center={center}
                zoomControl={true}
                {...restProps}
            >
                {renderMapContent()}
                {renderInfoWindow()}
            </GoogleMap>
        );
    }

    return <>{!isLoading ? <>{renderContent}</> : null}</>;
}

Map.propTypes = {
    // id for the map element, in order to target it directly
    id: PropTypes.string,

    // determines the zoom level of map
    zoom: PropTypes.number,

    // data points to be shown on the map, contains data for markers and infoWindows
    dataPoints: PropTypes.oneOfType([
        // if data points are markers then the propTypes will be an array of shape with marker properties
        PropTypes.arrayOf(
            PropTypes.shape({
                // id that uniquely idenitifies a marker, to be used a key as well
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

                // Marker props
                ...markerProps,

                // content for the infoWindow
                windowContent: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.node,
                ]),
            }),
        ),
        // if data points are to be drawn as a polyline then the PropTypes will be an array of lat long objects
        PropTypes.arrayOf(
            PropTypes.shape({
                lat: PropTypes.string,
                long: PropTypes.string,
            }),
        ),
    ]),
    // initial centering position of the map
    center: PropTypes.shape({
        // latitude used to center the map
        lat: PropTypes.number,

        // longitude used to center the map
        lng: PropTypes.number,
    }),

    // indicates whether the data is being loaded
    isLoading: PropTypes.bool,
    // indicates whether the data it to be drawn as a polyline
    drawPolyline: PropTypes.bool,

    // indicates whether to show normal map or heatmap
    showHeatMap: PropTypes.bool,
    showError: PropTypes.bool,
    markerPoints: PropTypes.array,
    showMarkerWithPolyline: PropTypes.bool,
    markerClickable: PropTypes.bool,
};

Map.defaultProps = {
    id: 'litter-map',
    zoom: 2.4,
    // by default, use 0,0 as centre point to show global map
    center: {
        lat: 0,
        lng: 0,
    },
    showHeatMap: false,
    showError: false,
    isLoading: false,
    drawPolyline: false,
    showMarkerInPolyline: false,
    markerClickable: true,
};

export default memo(Map);
